/**
 * API Client for Consenger AI Service
 * Handles streaming chat, single prompt, and TTS requests
 */

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatRequest {
  messages: ChatMessage[];
  max_tokens?: number;
  temperature?: number;
  stream?: boolean;
  model?: string;
}

export interface ChatResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message?: ChatMessage;
    delta?: { content?: string };
    finish_reason: string | null;
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface StreamEvent {
  type: 'token' | 'done' | 'error';
  content?: string;
  error?: {
    message: string;
    code: string;
    type: string;
  };
}

export interface TTSResponse {
  audioUrl: string;
  lipSyncUrl: string;
  lipSyncData?: any;
  format: 'mp3';
  duration: number;
  sampleRate: number;
  cached: boolean;
  provider: 'azure' | 'elevenlabs' | 'cache';
}

export interface TTSHistoryEntry {
  cacheKey: string;
  params: {
    voice?: string;
    rate?: number;
    pitch?: number;
    provider?: string;
    modelId?: string;
    stability?: number;
    similarityBoost?: number;
    style?: number;
    useSpeakerBoost?: boolean;
    visemeType?: 'facial-expression' | 'svg';
    format?: 'mp3' | 'pcm';
  };
  createdAt: string;
  provider: string;
}

export interface LiveKitTokenResponse {
  token: string;
  url: string;
  room: string;
  identity: string;
  expiresAt?: number;
}

export interface LiveKitDispatchResponse {
  dispatch: unknown;
  reused: boolean;
  room: string;
  agentName: string;
}

export class APIClient {
  private baseUrl: string;
  private token: string | null = null;
  private refreshCallback?: () => Promise<string | null>;

  constructor(
    baseUrl: string = 'https://api.consenger.app',
    refreshCallback?: () => Promise<string | null>
  ) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.refreshCallback = refreshCallback;
  }

  setToken(token: string | null) {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  /**
   * Manually trigger token refresh
   */
  async refreshToken(): Promise<string | null> {
    if (this.refreshCallback) {
      try {
        const newToken = await this.refreshCallback();
        if (newToken) {
          this.setToken(newToken);
        }
        return newToken;
      } catch (e) {
        console.error('[APIClient] Manual refresh failed:', e);
        return null;
      }
    }
    return this.token;
  }

  /**
   * Stream chat request with SSE
   */
  async *streamChat(
    messages: ChatMessage[],
    maxTokens: number = 2048,
    temperature: number = 0.7,
    model: string = 'consenger/conversation'
  ): AsyncGenerator<StreamEvent> {
    const request: ChatRequest = {
      model,
      messages,
      max_tokens: maxTokens,
      temperature,
      stream: true,
    };

    const response = await fetch(`${this.baseUrl}/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(`API Error: ${response.status} - ${error.message || 'Unknown'}`);
    }

    if (!response.body) {
      throw new Error('No response body for streaming');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          if (!line.startsWith('data: ')) continue;

          const data = line.slice(6);
          if (data === '[DONE]') {
            yield { type: 'done' };
            return;
          }

          try {
            const event: StreamEvent = JSON.parse(data);
            yield event;
          } catch (e) {
            console.error('Failed to parse SSE event:', data, e);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Single prompt request (non-streaming)
   */
  async singlePrompt(
    messages: ChatMessage[],
    maxTokens: number = 2048,
    temperature: number = 0.7,
    model: string = 'consenger/conversation'
  ): Promise<ChatResponse> {
    const request: ChatRequest = {
      model,
      messages,
      max_tokens: maxTokens,
      temperature,
      stream: false,
    };

    const response = await fetch(`${this.baseUrl}/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(`API Error: ${response.status} - ${error.message || 'Unknown'}`);
    }

    return response.json();
  }

  /**
   * TTS request (assuming Bedrock Polly or similar)
   * Note: Adjust endpoint based on actual API implementation
   */
  async textToSpeech(
    text: string,
    voice: string = 'en-US-JennyNeural',
    force: boolean = false,
    visemeType: 'facial-expression' | 'svg' = 'facial-expression',
    rate: number = 1.0,
    pitch: number = 0,
    provider: 'azure' | 'elevenlabs' = 'azure',
    modelId?: string,
    language?: string,
    elevenLabsOptions?: { stability?: number, similarityBoost?: number, style?: number, useSpeakerBoost?: boolean }
  ): Promise<TTSResponse> {
    if (!this.token) {
      throw new Error('No authentication token available. Please log in.');
    }

    // Use explicitly supplied language when available.
    // Fallback to parsing locale from voice ID, then default to en-US.
    const languageMatch = voice.match(/^([a-z]{2}-[A-Z]{2})/);
    const resolvedLanguage = language || (languageMatch ? languageMatch[1] : 'en-US');

    const response = await fetch(`${this.baseUrl}/v1/tts/synthesize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify({
        text,
        voice,
        language: resolvedLanguage,
        provider,
        force,
        visemeType,
        rate,
        pitch,
        modelId,
        ...elevenLabsOptions
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(`TTS Error: ${response.status} - ${error.message || 'Unknown'}`);
    }

    return response.json();
  }

  /**
   * Get available voices from TTS service
   */
  async getVoices(provider: 'azure' | 'elevenlabs' | 'all' = 'all', language?: string): Promise<Record<string, any[]>> {
    const params = new URLSearchParams({ provider });
    if (language) params.set('language', language);

    const response = await fetch(`${this.baseUrl}/v1/tts/voices?${params.toString()}`, {
      method: 'GET',
      headers: {
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
    });

    if (!response.ok) {
      console.warn('Failed to fetch voices');
      return {};
    }

    return response.json();
  }

  /**
   * Get TTS history for a specific text
   */
  async getHistory(text: string): Promise<TTSHistoryEntry[]> {
    if (!this.token) {
      throw new Error('Authentication required. Please sign in to view history.');
    }

    const response = await fetch(`${this.baseUrl}/v1/tts/history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      console.warn('Failed to fetch history');
      return [];
    }

    return response.json();
  }

  /**
   * Delete a TTS history entry
   */
  async deleteHistory(text: string, cacheKey: string): Promise<void> {
    if (!this.token) {
      throw new Error('Authentication required. Please sign in to delete history.');
    }

    await fetch(`${this.baseUrl}/v1/tts/history`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify({ text, cacheKey }),
    });
  }

  /**
   * Get current user tier info
   */
  async getTier(): Promise<{ tier: string; expiresAt?: string }> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    const { data } = await this.get<{ tier: string; expiresAt?: string }>('/v1/state/tier');
    return data;
  }

  /**
   * Generic GET request
   */
  async get<T = any>(path: string): Promise<{ status: number; data: T }> {
    return this.enhancedFetch(path, 'GET');
  }

  async post<T = any>(path: string, body: any): Promise<{ status: number; data: T }> {
    return this.enhancedFetch(path, 'POST', body);
  }

  async put<T = any>(path: string, body: any): Promise<{ status: number; data: T }> {
    return this.enhancedFetch(path, 'PUT', body);
  }

  async delete<T = any>(path: string): Promise<{ status: number; data: T }> {
    return this.enhancedFetch(path, 'DELETE');
  }

  /**
   * Enhanced fetch with auto-refresh logic
   */
  private async enhancedFetch<T = any>(
    path: string,
    method: string,
    body?: any,
    retryCount = 0
  ): Promise<{ status: number; data: T }> {
    const performRequest = async (tokenOverride?: string) => {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(tokenOverride || this.token ? { Authorization: `Bearer ${tokenOverride || this.token}` } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await response.json().catch(() => ({}));
      return { response, data };
    };

    let { response, data } = await performRequest();

    // Handle 401 Unauthorized - Attempt Refresh
    if (response.status === 401 && this.refreshCallback && retryCount < 1) {
      console.log('[APIClient] 401 detected, attempting refresh...');
      try {
        const newToken = await this.refreshCallback();
        if (newToken) {
          console.log('[APIClient] Refresh successful, retrying request...');
          this.setToken(newToken);
          // Retry with new token
          const retryResult = await performRequest(newToken);
          response = retryResult.response;
          data = retryResult.data;
        } else {
          console.warn('[APIClient] Refresh callback returned no token');
        }
      } catch (refreshError) {
        console.error('[APIClient] Refresh failed:', refreshError);
        // Fall through to throw original error
      }
    }

    if (!response.ok) {
      throw new Error(`${method} ${path} failed: ${response.status} - ${data.message || 'Unknown'}`);
    }

    return { status: response.status, data };
  }

  /**
   * Get LiveKit room token from API gateway.
   */
  async getLiveKitToken(
    room: string,
    identity: string,
    name?: string
  ): Promise<LiveKitTokenResponse> {
    if (!this.token) {
      throw new Error('Authentication required. Please sign in first.');
    }

    const params = new URLSearchParams({
      room,
      identity,
      name: name || identity,
    });

    const response = await fetch(`${this.baseUrl}/v1/livekit/token?${params.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(`LiveKit token error: ${response.status} - ${error.message || 'Unknown'}`);
    }

    return response.json();
  }

  /**
   * Create (or reuse) an explicit agent dispatch for a room.
   */
  async createLiveKitDispatch(
    room: string,
    options?: { agentName?: string; metadata?: string }
  ): Promise<LiveKitDispatchResponse> {
    if (!this.token) {
      throw new Error('Authentication required. Please sign in first.');
    }

    const response = await fetch(`${this.baseUrl}/v1/livekit/dispatch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify({
        room,
        agentName: options?.agentName,
        metadata: options?.metadata,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(`LiveKit dispatch error: ${response.status} - ${error.message || 'Unknown'}`);
    }

    return response.json();
  }
}
